import React, { type FormEvent, type ReactNode } from 'react';


type SidePanelFormLayoutProps =
  | ({
      onSubmit: (event?: FormEvent<HTMLFormElement>) => void;
      children: ReactNode;
    } & Omit<React.ComponentPropsWithoutRef<'form'>, 'onSubmit' | 'children'>)
  | ({
      onSubmit?: never;
      children: ReactNode;
    } & Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>);

interface SidePanelFormLayoutFieldsProps {
  children: ReactNode;
}

interface SidePanelFormLayoutFooterProps {
  children: ReactNode;
}

/**
 * SidePanelFormLayout.Fields - Grid container for form fields
 *
 * @example
 * <SidePanelFormLayout.Fields>
 *   <LabelInput label="Nombre" />
 *   <Selector label="Tipo" options={options} />
 * </SidePanelFormLayout.Fields>
 */
const SidePanelFormLayoutFields: React.FC<SidePanelFormLayoutFieldsProps> = ({ children }) => (
  <div className="grid grid-cols-1 gap-6">
    {children}
  </div>
);

/**
 * SidePanelFormLayout.Footer - Centered button container
 *
 * @example
 * <SidePanelFormLayout.Footer>
 *   <PermissionGuard action={isEditMode ? 'edit' : 'create'}>
 *     <PrimaryButton type="submit" fullWidth isLoading={isSubmitting}>
 *       {translate('common:actions.save')}
 *     </PrimaryButton>
 *   </PermissionGuard>
 * </SidePanelFormLayout.Footer>
 */
const SidePanelFormLayoutFooter: React.FC<SidePanelFormLayoutFooterProps> = ({ children }) => (
  <div className="flex justify-center pt-4">
    {children}
  </div>
);

/**
 * SidePanelFormLayout - Form layout for side panel forms
 *
 * @example
 * <SidePanelFormLayout onSubmit={handleSubmit}>
 *   <SidePanelFormLayout.Fields>
 *     <LabelInput label="Nombre" {...register('name')} />
 *     <Selector label="Área" options={areaOptions} />
 *   </SidePanelFormLayout.Fields>
 *
 *   <SidePanelFormLayout.Footer>
 *     <PrimaryButton type="submit" fullWidth isLoading={isSubmitting}>
 *       Guardar
 *     </PrimaryButton>
 *   </SidePanelFormLayout.Footer>
 * </SidePanelFormLayout>
 */
export const SidePanelFormLayout: React.FC<SidePanelFormLayoutProps> & {
  Fields: typeof SidePanelFormLayoutFields;
  Footer: typeof SidePanelFormLayoutFooter;
} = ({
  onSubmit,
  children,
  className = '',
  ...restProps
}) => {
  const baseClassName = `flex flex-col gap-4 ${className}`.trim();

  if (onSubmit) {
    return (
      <form
        className={baseClassName}
        onSubmit={onSubmit}
        {...(restProps as React.ComponentPropsWithoutRef<'form'>)}
      >
        {children}
      </form>
    );
  }

  return (
    <div className={baseClassName} {...(restProps as React.ComponentPropsWithoutRef<'div'>)}>
      {children}
    </div>
  );
};

SidePanelFormLayout.Fields = SidePanelFormLayoutFields;
SidePanelFormLayout.Footer = SidePanelFormLayoutFooter;

export default SidePanelFormLayout;
